import { useState, useEffect, useCallback } from 'react';
import { apiImageUrl } from '../utils/streamApi';

/**
 * Zoomed view of a face capture, with ‹ › / arrow-key navigation across the
 * person's other captures.
 *
 * Shared by the Known and Banned faces pages — `kind` picks which photo route
 * to fetch the neighbours from. Banned captures carry no day/night profile, so
 * the scene badge only renders when the photo actually has one.
 */
export default function FacePhotoLightbox({ photos, idx: initialIdx, initialSrc,
                                            personName, kind = 'known', onClose }) {
  const [idx, setIdx] = useState(initialIdx);
  const [src, setSrc] = useState(initialSrc);

  const navigate = useCallback((dir) => {
    const next = idx + dir;
    if (next < 0 || next >= photos.length) return;
    setSrc(null);
    apiImageUrl(`/ui/faces/${kind}/${encodeURIComponent(personName)}/photo/${encodeURIComponent(photos[next].file)}`)
      .then(url => setSrc(url));
    setIdx(next);
  }, [idx, photos, personName, kind]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight') navigate(1);
      if (e.key === 'ArrowLeft')  navigate(-1);
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [navigate, onClose]);

  const photo = photos[idx];
  const navBtn = (dir, label, side) => (
    <button onClick={e => { e.stopPropagation(); navigate(dir); }}
      style={{
        position: 'fixed', [side]: 16, top: '50%', transform: 'translateY(-50%)',
        background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '50%', width: 44, height: 44, color: '#fff', fontSize: 22,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10001, backdropFilter: 'blur(4px)',
      }}>{label}</button>
  );

  return (
    <div className="pk-lightbox" onClick={onClose}>
      <button className="pk-lightbox-close" onClick={onClose}>✕</button>
      {idx > 0 && navBtn(-1, '‹', 'left')}
      {src
        ? <img src={src} alt="face" className="pk-lightbox-img" onClick={e => e.stopPropagation()} />
        : <div style={{ color: '#666', fontSize: 14 }}>Loading…</div>
      }
      {idx < photos.length - 1 && navBtn(1, '›', 'right')}
      <div className="pk-lightbox-meta" onClick={e => e.stopPropagation()}>
        {photo?.profile && (
          <span className={`badge ${photo.profile === 'night' ? 'badge-night' : photo.profile === 'auto' ? 'badge-auto' : 'badge-day'}`}>
            {photo.profile === 'night' ? '🌙 Night' : photo.profile === 'auto' ? '🤖 Auto' : '☀️ Day'}
          </span>
        )}
        <span style={{ fontSize: 12, color: '#aaa' }}>{personName}</span>
        <span style={{ fontSize: 11, color: '#666', marginLeft: 8 }}>{idx + 1} / {photos.length}</span>
      </div>
    </div>
  );
}
